import { type FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AdsContext } from '@components/Ads/context';

import { useClaimDailyTgemBonusMutation } from '@/api/queries/user';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { secondsUntilEndOfDayGMT } from '@/common/lib/secondsUntilEndOfDay';
import { ShopBuyButton } from '@/components/Shop/ui/ShopUi';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import {
  claimDailyTgemBonus,
  clearDailyTgemBonus,
  updateCoinBalance,
} from '@/redux/reducers/userReducer';
import { selectDailyTgemBonus } from '@/redux/selectors/userSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { TgemCard } from '../TgemCard/TgemCard';

import tgemPng from '/img/shop/v2/tgem-icon.png';
import tgemWebp from '/img/shop/v2/tgem-icon.webp';
import vipPng from '/img/VIP/vip-icon.png';
import watchPng from '/img/watch.png';

const formatCooldown = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}h : ${String(minutes).padStart(2, '0')}m`;
};

/** +1 T-Gem a day: free, then one more for an ad (no ad for VIP). */
const DailyTgemCard: FC = () => {
  const { t } = useTranslation();
  const dailyTgemsBonus = useAppSelector(selectDailyTgemBonus);
  const hasVipStatus = useAppSelector((state) => state.user.user?.VIP);
  const secondToEndDay = useCountDownTimer(secondsUntilEndOfDayGMT());
  const [claimDailyBonus, { isLoading }] = useClaimDailyTgemBonusMutation();
  const { showAd, adsIsLoading } = useContext(AdsContext);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (secondToEndDay <= 0) {
      dispatch(clearDailyTgemBonus());
    }
  }, [secondToEndDay]);

  const claimBonus = (type: 'free' | 'advertising'): void => {
    claimDailyBonus({ type })
      .unwrap()
      .then(({ tgem }) => {
        dispatch(claimDailyTgemBonus(type));
        dispatch(updateCoinBalance(tgem));
      });
  };

  const claimAdsBonus = (): void => {
    if (hasVipStatus) {
      claimBonus('advertising');
    } else {
      showAd({
        placements: 'daily',
        onReward: () => {
          claimBonus('advertising');
        },
        onError: () => {
          showToast(t('Ads launch error'));
        },
      });
    }
  };

  const isFree = !!dailyTgemsBonus?.free;
  const isAds = !isFree && !!dailyTgemsBonus?.advertising;
  const inactive = !isFree && !isAds;

  return (
    <TgemCard
      amount={1}
      caption="Daily Bonus"
      inactive={inactive}
      marker={isFree}
      art={
        <>
          <Picture
            img={tgemPng}
            webp={tgemWebp}
            alt=""
            className="absolute inset-0 block"
          />
          <Picture
            img={tgemPng}
            webp={tgemWebp}
            alt="t-gem"
            className="absolute inset-0 block"
          />
        </>
      }
    >
      {isFree && (
        <ShopBuyButton
          label={t('Free')}
          tag={t('DAILY')}
          isLoading={isLoading}
          disabled={isLoading}
          onClick={() => claimBonus('free')}
        />
      )}
      {isAds && (
        <ShopBuyButton
          label={hasVipStatus ? t('Free') : t('Watch')}
          icon={
            <img
              src={hasVipStatus ? vipPng : watchPng}
              alt=""
              className="size-6"
            />
          }
          isLoading={isLoading || adsIsLoading}
          disabled={isLoading || adsIsLoading}
          onClick={claimAdsBonus}
        />
      )}
      {inactive && (
        <ShopBuyButton
          variant="locked"
          label={formatCooldown(secondToEndDay)}
          disabled
        />
      )}
    </TgemCard>
  );
};

export { DailyTgemCard };
